import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Certificate, Calendar, ArrowRight, Warning, Plus, X, Microphone, Stop, Trash, Stethoscope, TestTube, FirstAidKit, UsersThree, Pill, Ambulance, Clock, CheckCircle,
} from "@phosphor-icons/react";
import { ISSUE_TAGS } from "../../lib/grievanceTags";
import { custApi } from "../../lib/customerApi";
import { useCustAuth } from "../../lib/customerAuth";
import { toast } from "sonner";
import MeHomeBanners from "./MeHomeBanners";
import AmbulanceMiniTracker from "../../components/AmbulanceMiniTracker";
import { useCustomerCms } from "../../lib/useCustomerCms";
import { HomeGreeting, BmwBookCard, NextCollectionCard, LivePickupCard, QuickServices, WalletCard, DuesCard, SectionLabel } from "./MeHomeCards";

const MOODS = [
  { key: "normal",    label: "Fine",       emoji: "🙂" },
  { key: "cool",      label: "Neutral",    emoji: "😐" },
  { key: "angry",     label: "Frustrated", emoji: "😠" },
  { key: "very_bad",  label: "Very upset", emoji: "😡" },
];

const ISSUE_TAGS_LOCAL = ISSUE_TAGS;  // re-alias for existing map calls


export default function MeDashboard() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [me, setMe] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [specialDates, setSpecialDates] = useState([]);
  const [, setBagSummary] = useState(null);
  const [, setDeliveryAlert] = useState(null);
  const [reqOpen, setReqOpen] = useState(false);
  const [srs, setSrs] = useState([]);
  const cms = useCustomerCms();
  const { customer } = useCustAuth();
  // Iter 220f — mirror the routing gate on the home page so pending /
  // rejected customers can't sneak into hygiene flows via the tiles
  // (Biomedical Waste, Buy Bags, Gloves, Request-a-pickup, Upcoming
  // pickup card, "Buy Bags" CTA at the bottom, etc). Tiles marked
  // `hygiene: true` are routed to `/me/hygiene` instead so the
  // customer sees the friendly verification lock screen with the
  // Chat / Contact-us CTAs.
  const custStatus = (customer?.status || "").toLowerCase();
  const isUnverified = custStatus && custStatus !== "active";
  const hygieneGate = "/me/hygiene";

  const loadBags = () => {
    custApi.get("/me/bags/summary").then(r => setBagSummary(r.data)).catch(() => {});
    custApi.get("/me/bags/delivery-alerts")
      .then(r => setDeliveryAlert(r.data)).catch(() => {});
  };

  useEffect(() => {
    custApi.get("/me/summary")
      .then((r) => setS(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load"));
    custApi.get("/me").then(r => setMe(r.data)).catch(() => {});
    custApi.get("/me/next-collection-dates").then(r => setUpcoming(r.data)).catch(() => {});
    custApi.get("/me/service-requests").then(r => setSrs(Array.isArray(r.data) ? r.data : (r.data?.rows || []))).catch(() => {});
    custApi.get("/me/special-collection-dates")
      .then(r => setSpecialDates(r.data?.rules || []))
      .catch(() => {});
    loadBags();
  }, []);

  // Iter 153/155 — foreground GPS ping loop. High-accuracy mode, and
  // client-side reject anything worse than 25m so the EagleEye map
  // stays crisp. Ticks every 90s while the tab is visible.
  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    let cancelled = false;
    const push = () => {
      if (document.hidden) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          const acc = pos.coords.accuracy;
          if (acc && acc > 25) return;  // skip low-accuracy fixes
          custApi.post("/me/location/ping", {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy_m: acc || null,
          }).catch(() => { /* silent — best-effort */ });
        },
        () => { /* permission denied or timeout — silent */ },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 },
      );
    };
    push();
    const iv = setInterval(push, 90_000);
    document.addEventListener("visibilitychange", push);
    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", push);
    };
  }, []);

  if (err) return <div className="text-sm text-[#C25D38] px-5 py-6">{err}</div>;
  if (!s) return <div className="text-sm text-[#8A8780] px-5 py-6">Loading…</div>;

  const shortName = (me?.name || customer?.name || "").split(" ")[0] || "there";

  return (
    <div className="font-body bg-[#FCFCFB] pb-6" data-testid="me-dashboard">
      <HomeGreeting name={shortName} />
      <BmwBookCard locked={isUnverified} gate={hygieneGate} />
      <NextCollectionCard me={me} upcoming={upcoming} srs={srs} />
      <LivePickupCard srs={srs} locked={isUnverified} />
      <QuickServices locked={isUnverified} gate={hygieneGate} onRequestBags={() => setReqOpen(true)} />
      <WalletCard balance={s?.wallet} />
      <DuesCard dues={s?.dues} />

      <SectionLabel>For you</SectionLabel>
      <TodaysCareStrip/>
      {cms.isFeatureVisible("home", "ads") && <MeHomeBanners/>}
      <HealthDailyStrip/>
      <FamilyDashboardStrip/>

      {specialDates.length > 0 && (
        <section className="px-5 mt-4" data-testid="me-special-dates">
          <div className="bg-white rounded-[22px] border border-[#F1EFEC] shadow-[0_6px_24px_rgba(17,24,39,0.05)] p-4">
            <div className="font-display font-bold text-[16px] text-[#111827] flex items-center gap-2">
              <Calendar size={16} weight="duotone" className="text-[#DC2626]" /> Special collection days
            </div>
            <div className="space-y-3 mt-3">
              {specialDates.map(r => (
                <div key={r.id}>
                  <div className="font-semibold text-[14px] text-[#111827]">{r.title}</div>
                  {r.notes && <div className="text-[12px] text-[#6B7280] mt-0.5 whitespace-pre-wrap">{r.notes}</div>}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {r.dates.slice(0, 6).map(d => (
                      <span key={d} className="text-[12px] bg-[#FFF1F1] text-[#DC2626] px-2.5 py-1 rounded-full">{formatSpecialDate(d)}</span>
                    ))}
                    {r.dates.length > 6 && <span className="text-[12px] text-[#6B7280] self-center">+{r.dates.length - 6} more</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="px-5 mt-4"><GrievanceLauncher /></div>

      {reqOpen && <BagRequestModal onClose={() => setReqOpen(false)} onCreated={() => { setReqOpen(false); loadBags(); }}/>}
    </div>
  );
}


function formatSpecialDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    const day = d.getDate().toString().padStart(2, "0");
    const mon = d.toLocaleString("en-US", { month: "short" });
    const wd = d.toLocaleString("en-US", { weekday: "short" });
    return `${wd} · ${day} ${mon}`;
  } catch { return iso; }
}

const BAG_REQUEST_ACK_VERSION = "2026-07-11-v1";
const BMW_PALETTE = [
  { v: "yellow", label: "Yellow", swatch: "#F5C518" },
  { v: "blue",   label: "Blue",   swatch: "#1E5FA0" },
  { v: "red",    label: "Red",    swatch: "#C0392B" },
  { v: "white",  label: "White",  swatch: "#F4F4F4" },
];
const GEN_PALETTE = [
  { v: "black",  label: "Black",  swatch: "#1A1A1A" },
  { v: "grey",   label: "Grey",   swatch: "#7A7A7A" },
];
const QTY_OPTIONS = [10, 20, 50, 100];

export function BagRequestModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    waste_category: "BMW", colour_code: "yellow",
    quantity: 10, notes: "",
    acknowledged: true,
    payment_mode: "cod",
  });
  const [busy, setBusy] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [pricing, setPricing] = useState(null);       // {unit_price,total}
  const [result, setResult] = useState(null);         // post-submit result
  const [checking, setChecking] = useState(false);
  const palette = f.waste_category === "BMW" ? BMW_PALETTE : GEN_PALETTE;
  useEffect(() => {
    if (!palette.some(c => c.v === f.colour_code)) {
      setF(s => ({ ...s, colour_code: palette[0].v }));
    }
  }, [f.waste_category, palette, f.colour_code]);
  // Live bill preview — hits the backend whenever inputs change.
  useEffect(() => {
    let cancelled = false;
    if (result) return; // frozen after submit
    (async () => {
      try {
        const r = await custApi.get("/me/bag-requests/pricing", {
          params: {
            waste_category: f.waste_category,
            colour_code: f.colour_code,
            quantity: f.quantity,
          },
        });
        if (!cancelled) setPricing(r.data);
      } catch { /* silent — form still works with unknown total */ }
    })();
    return () => { cancelled = true; };
  }, [f.waste_category, f.colour_code, f.quantity, result]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.acknowledged) {
      toast.error("Please accept the acknowledgement to continue.");
      return;
    }
    setBusy(true);
    try {
      const r = await custApi.post("/me/bag-requests", {
        ...f,
        acknowledgement_version: BAG_REQUEST_ACK_VERSION,
      });
      setResult(r.data);
      if (f.payment_mode === "online" && r.data.payment_link_url) {
        window.open(r.data.payment_link_url, "_blank", "noopener,noreferrer");
        toast.success("Opened payment page — complete payment to finish.");
      } else {
        toast.success(`Request ${r.data.request_no || "submitted"} — cash on delivery.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    }
    setBusy(false);
  };
  const refreshPayment = async () => {
    if (!result?.id) return;
    setChecking(true);
    try {
      const r = await custApi.post(`/me/bag-requests/${result.id}/refresh-payment`);
      if (r.data.payment_status === "paid") {
        toast.success("Payment confirmed ✓");
        setResult(s => ({ ...s, payment_status: "paid" }));
        // Let the parent refresh the request list, then close.
        setTimeout(() => { onCreated(); }, 900);
      } else {
        toast.info("Not paid yet — try again in a moment.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not refresh");
    }
    setChecking(false);
  };
  if (result && f.payment_mode === "online") {
    const paid = result.payment_status === "paid";
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-3 overflow-y-auto">
        <div className="bg-white rounded-t-md md:rounded-md w-full max-w-md my-auto" data-testid="me-bag-req-payment">
          <div className="p-4 border-b border-[#E5E2D9]">
            <div className="font-heading text-lg">
              {paid ? "Payment received" : "Complete your payment"}
            </div>
            <div className="text-[11px] text-[#5C5A55]">
              {paid ? "Your bag request is confirmed. Admin will approve and schedule delivery." : "Pay via UPI · Cards · Netbanking · Wallets on the Razorpay page."}
            </div>
          </div>
          <div className="p-4 space-y-3">
            {result.request_no && <div className="flex items-center justify-between bg-[#F7F6F2] rounded px-3 py-2" data-testid="me-bag-req-no-block">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">Request no.</div>
                <div className="font-mono text-sm text-[#DC2626]"
                      data-testid="me-bag-req-no">{result.request_no}</div>
              </div>
              <div className="text-[10px] text-[#5C5A55] text-right">
                Track this ID<br/>for your bag delivery.
              </div>
            </div>}
            <div className="bg-[#F7F6F2] rounded p-3 text-sm">
              <div className="flex justify-between"><span className="text-[#5C5A55]">Total</span><span className="font-mono font-semibold">₹{result.total_amount?.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#5C5A55]">Status</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${paid ? "text-[#1B5E20]" : "text-[#7A5A11]"}`}>
                  {paid ? "PAID" : "UNPAID"}
                </span>
              </div>
            </div>
            {result.delivery_pin && <div className="bg-[#F9F0D6] border border-[#F5C518]/60 rounded p-3" data-testid="me-bag-req-pin-block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A5A11] font-semibold">
                Delivery PIN
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <div className="font-mono text-2xl font-bold text-[#1A1A1A] tracking-[0.35em]"
                      data-testid="me-bag-req-pin">
                  {result.delivery_pin}
                </div>
                <div className="text-[10px] text-[#7A5A11] text-right leading-tight">
                  Share this PIN with the delivery<br/>staff to complete handover.
                </div>
              </div>
            </div>}
            {!paid && result.payment_link_url && (
              <a href={result.payment_link_url} target="_blank" rel="noopener noreferrer"
                 data-testid="me-bag-req-open-pay"
                 className="h-10 flex items-center justify-center bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded text-sm font-medium">
                Open Razorpay payment page →
              </a>
            )}
          </div>
          <div className="p-4 border-t border-[#E5E2D9] flex justify-end gap-2">
            <button type="button" onClick={onCreated} className="h-10 px-4 border border-[#E5E2D9] rounded text-sm">Close</button>
            {!paid && (
              <button type="button" onClick={refreshPayment} disabled={checking}
                      data-testid="me-bag-req-refresh-pay"
                      className="h-10 px-5 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded text-sm font-medium">
                {checking ? "Checking…" : "I've paid — refresh"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // COD submitted — dedicated confirmation screen (previously the form
  // just closed with a toast).
  if (result && f.payment_mode === "cod") {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-3 overflow-y-auto">
        <div className="bg-white rounded-t-md md:rounded-md w-full max-w-md my-auto" data-testid="me-bag-req-cod">
          <div className="p-4 border-b border-[#E5E2D9]">
            <div className="font-heading text-lg">Request submitted ✓</div>
            <div className="text-[11px] text-[#5C5A55]">
              Cash on delivery — pay the field staff when your bags arrive.
            </div>
          </div>
          <div className="p-4 space-y-3">
            {result.request_no && <div className="flex items-center justify-between bg-[#F7F6F2] rounded px-3 py-2" data-testid="me-bag-req-no-block">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">Request no.</div>
                <div className="font-mono text-sm text-[#DC2626]"
                      data-testid="me-bag-req-no">{result.request_no}</div>
              </div>
              <div className="text-[10px] text-[#5C5A55] text-right">
                Track this ID<br/>for your bag delivery.
              </div>
            </div>}
            <div className="bg-[#F7F6F2] rounded p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[#5C5A55]">Amount due on delivery</span>
                <span className="font-mono font-semibold">₹{result.total_amount?.toFixed(2)}</span>
              </div>
            </div>
            {result.delivery_pin && <div className="bg-[#F9F0D6] border border-[#F5C518]/60 rounded p-3" data-testid="me-bag-req-pin-block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A5A11] font-semibold">
                Delivery PIN
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <div className="font-mono text-2xl font-bold text-[#1A1A1A] tracking-[0.35em]"
                      data-testid="me-bag-req-pin">
                  {result.delivery_pin}
                </div>
                <div className="text-[10px] text-[#7A5A11] text-right leading-tight">
                  Share this PIN with the delivery<br/>staff to complete handover.
                </div>
              </div>
            </div>}
          </div>
          <div className="p-4 border-t border-[#E5E2D9] flex justify-end">
            <button type="button" onClick={onCreated}
                    data-testid="me-bag-req-cod-done"
                    className="h-10 px-5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded text-sm font-medium">
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-3 overflow-y-auto">
      <form onSubmit={submit} className="bg-white rounded-t-md md:rounded-md w-full max-w-md my-auto" data-testid="me-bag-req-modal">
        <div className="p-4 border-b border-[#E5E2D9]">
          <div className="font-heading text-lg">Request New Bags</div>
          <div className="text-[11px] text-[#5C5A55]">Admin will approve and assign QR-coded bags.</div>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">Waste Category</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[{v:"BMW",l:"Biomedical (BMW)"},{v:"GENERAL",l:"General"}].map(o => (
                <button key={o.v} type="button" onClick={() => setF(s => ({...s, waste_category: o.v}))}
                        data-testid={`me-bag-req-cat-${o.v.toLowerCase()}`}
                        className={`h-10 px-3 border rounded text-sm transition-colors ${
                          f.waste_category === o.v
                            ? "bg-[#DC2626] text-white border-[#DC2626]"
                            : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">Bag Colour · CPCB coding</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {palette.map(c => (
                <button key={c.v} type="button" onClick={() => setF(s => ({...s, colour_code: c.v}))}
                        data-testid={`me-bag-req-colour-${c.v}`}
                        className={`h-10 px-3 pr-3.5 inline-flex items-center gap-2 border rounded text-sm transition-colors ${
                          f.colour_code === c.v
                            ? "border-[#DC2626] ring-2 ring-[#DC2626]/30"
                            : "border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                  <span className="w-4 h-4 rounded-full border border-[#00000020]" style={{ background: c.swatch }}/>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">Quantity</label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              {QTY_OPTIONS.map(q => (
                <button key={q} type="button" onClick={() => setF(s => ({...s, quantity: q}))}
                        data-testid={`me-bag-req-qty-${q}`}
                        className={`h-10 border rounded text-sm font-mono transition-colors ${
                          f.quantity === q
                            ? "bg-[#DC2626] text-white border-[#DC2626]"
                            : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                  {q}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">Notes (optional)</label>
            <textarea rows={2} value={f.notes} onChange={e => setF(s => ({...s, notes: e.target.value}))}
                      data-testid="me-bag-req-notes"
                      className="w-full px-3 py-2 border border-[#E5E2D9] rounded mt-1 text-sm"/>
          </div>
          {/* Live bill preview */}
          <div className="border border-[#DC2626]/30 rounded p-3 bg-[#EFF3EE]" data-testid="me-bag-req-bill">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">Bill preview</div>
            <div className="flex justify-between mt-1 text-sm">
              <span className="text-[#3A3A3A]">
                {pricing ? `${pricing.quantity} bags × ₹${pricing.unit_price.toFixed(2)}` : "Calculating…"}
              </span>
              <span className="font-mono font-semibold text-[#DC2626]">
                ₹{pricing ? pricing.total.toFixed(2) : "—"}
              </span>
            </div>
          </div>
          {/* Payment mode */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">Payment</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[
                {v:"online", l:"Pay online", h:"UPI · Cards · Wallets"},
                {v:"cod",    l:"Cash on delivery", h:"Pay to staff on handover"},
              ].map(o => (
                <button key={o.v} type="button" onClick={() => setF(s => ({...s, payment_mode: o.v}))}
                        data-testid={`me-bag-req-pay-${o.v}`}
                        className={`text-left p-2 border rounded transition-colors ${
                          f.payment_mode === o.v
                            ? "border-[#DC2626] ring-2 ring-[#DC2626]/30"
                            : "border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                  <div className="text-sm font-medium">{o.l}</div>
                  <div className="text-[10px] text-[#5C5A55]">{o.h}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="bg-[#F7F6F2] border border-[#E5E2D9] rounded p-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox"
                     checked={f.acknowledged}
                     onChange={e => setF(s => ({...s, acknowledged: e.target.checked}))}
                     data-testid="me-bag-req-ack"
                     className="mt-1 accent-[#DC2626]"/>
              <span className="text-[12px] leading-snug text-[#1A1A1A]">
                I accept the <span className="font-semibold">QR-Coded Bag Request &amp; Delivery Acknowledgement</span>.
                <button type="button" onClick={() => setShowFull(v => !v)}
                        data-testid="me-bag-req-readmore"
                        className="ml-1 underline text-[#DC2626]">
                  {showFull ? "Hide" : "Read more"}
                </button>
              </span>
            </label>
            {showFull && (
              <div className="mt-3 max-h-56 overflow-y-auto text-[11px] leading-relaxed text-[#3A3A3A] space-y-2 pr-1"
                   data-testid="me-bag-req-declaration">
                <p className="font-semibold text-[12px] text-[#1A1A1A]">QR-Coded Bag Issue Declaration</p>
                <p>I acknowledge receipt of the QR-coded waste collection bags issued by Rescigo Health. I understand and agree that:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Each QR-coded bag carries a unique identity assigned to my customer account.</li>
                  <li>The QR-coded bags remain assigned exclusively to my registered account until officially collected by Rescigo Health.</li>
                  <li>I shall store all issued bags securely.</li>
                  <li>I shall not exchange, transfer, lend, sell, or hand over the QR-coded bags to any unauthorized person, organization, or waste collector.</li>
                  <li>I shall not tamper with, alter, remove, or damage the QR labels.</li>
                </ul>
                <p className="font-semibold text-[12px] text-[#1A1A1A] pt-1">Customer Responsibility Before Collection</p>
                <p>Until the QR-coded bag is scanned and officially accepted by an authorized Rescigo Health collection staff, the custody and security of the bag and its contents remain entirely my responsibility.</p>
                <p className="font-semibold text-[12px] text-[#1A1A1A] pt-1">Unauthorized Transfer</p>
                <p>I understand that handing over Rescigo-issued QR-coded bags to unauthorized individuals, agencies, or waste handlers may:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>break the digital Chain of Custody,</li>
                  <li>compromise waste traceability,</li>
                  <li>affect regulatory compliance,</li>
                  <li>result in suspension of my registration.</li>
                </ul>
                <p className="font-semibold text-[12px] text-[#1A1A1A] pt-1">Company&rsquo;s Responsibility</p>
                <p>Rescigo Health assumes responsibility for the waste only after:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>successful QR verification,</li>
                  <li>official collection by authorized staff,</li>
                  <li>digital acceptance into the Rescigo system.</li>
                </ul>
                <p>Any loss, misuse, theft, damage, substitution, or unauthorized transfer before official collection remains the responsibility of the customer.</p>
                <p className="font-semibold text-[12px] text-[#1A1A1A] pt-1">3. Chain of Custody Declaration</p>
                <p>I understand that the integrity of the QR-based Chain of Custody depends upon maintaining the assigned QR-coded bags without alteration. I agree that:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>only the original assigned QR bags shall be used,</li>
                  <li>no duplicate QR labels shall be created,</li>
                  <li>no QR labels shall be removed or replaced,</li>
                  <li>every collection shall be completed only through the official Rescigo platform.</li>
                </ul>
                <p>Failure to comply may result in suspension or cancellation of services.</p>
                <p className="font-semibold text-[12px] text-[#1A1A1A] pt-1">4. Digital Consent</p>
                <p>I consent to Rescigo Health:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>storing my registration information,</li>
                  <li>maintaining digital waste collection records,</li>
                  <li>generating electronic certificates,</li>
                  <li>generating electronic invoices,</li>
                  <li>using QR-based tracking,</li>
                  <li>capturing GPS coordinates during collection,</li>
                  <li>recording collection timestamps,</li>
                  <li>maintaining disposal records for regulatory compliance.</li>
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="p-4 border-t border-[#E5E2D9] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded text-sm">Cancel</button>
          <button type="submit" disabled={busy || !f.acknowledged} data-testid="me-bag-req-submit"
                  className="h-10 px-5 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded text-sm font-medium">
            {busy ? "Submitting…" : (f.payment_mode === "online"
              ? `Pay ₹${pricing ? pricing.total.toFixed(2) : "—"} & submit`
              : "Submit — pay on delivery")}
          </button>
        </div>
      </form>
    </div>
  );
}


export function GrievanceLauncher({ triggerText, initialOpen = false, hideButton = false, onSubmitted }) {
  const [open, setOpen] = useState(initialOpen);
  const [mood, setMood] = useState("normal");
  const [tags, setTags] = useState([]);
  const [issue, setIssue] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter 115 — voice grievance state
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceId, setVoiceId] = useState(null);
  const [voiceUploading, setVoiceUploading] = useState(false);

  const toggleTag = (t) =>
    setTags(cur => cur.includes(t) ? cur.filter(x => x !== t) : [...cur, t]);

  const reset = () => {
    setMood("normal"); setTags([]); setIssue("");
    setVoiceBlob(null); setVoiceId(null); setVoiceUploading(false);
  };
  const submit = async () => {
    if (!issue.trim() || issue.trim().length < 3) {
      toast.error("Please describe your grievance"); return;
    }
    setBusy(true);
    try {
      // If a voice blob is captured but not yet uploaded, upload now.
      let vid = voiceId;
      if (voiceBlob && !vid) {
        setVoiceUploading(true);
        const fd = new FormData();
        fd.append("file", voiceBlob, "grievance-voice.webm");
        // Let axios set the multipart boundary automatically —
        // manually setting Content-Type strips the boundary and
        // FastAPI rejects the upload.
        const up = await custApi.post("/me/grievance/voice", fd);
        vid = up.data.voice_id;
        setVoiceId(vid);
        setVoiceUploading(false);
      }
      const r = await custApi.post("/me/grievance", {
        issue: issue.trim(), mood, tags,
        voice_id: vid || null,
      });
      toast.success(`Grievance submitted · ${r.data.ticket_no || "Thanks"}`);
      reset(); setOpen(false);
      if (typeof onSubmitted === "function") onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
      setVoiceUploading(false);
    }
    setBusy(false);
  };

  return (
    <>
      {!hideButton && (
        <button data-testid="me-grievance-btn" onClick={() => setOpen(true)}
                className="w-full rounded-xl border-2 border-dashed border-[#C25D38]/40 bg-[#FFF6F2] hover:bg-[#FEE7DC] p-4 text-left transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#C25D38]/15 text-[#C25D38] flex items-center justify-center text-xl">
              <Warning size={22} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#1A1A1A]">{triggerText || "Submit a grievance"}</div>
              <div className="text-[11px] text-[#5C5A55]">Tell us what went wrong — we&apos;ll open a ticket right away.</div>
            </div>
            <ArrowRight size={16} className="text-[#C25D38]" />
          </div>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-2 sm:p-4"
             onClick={() => setOpen(false)}>
          <div onClick={e => e.stopPropagation()}
               className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto"
               data-testid="me-grievance-drawer">
            <div className="px-5 py-4 border-b border-[#E5E2D9] flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Rescigo Support</div>
                <div className="font-heading text-lg text-[#1A1A1A]">Submit a grievance</div>
              </div>
              <button onClick={() => setOpen(false)}
                      data-testid="me-grievance-close"
                      className="text-[#8A8780] hover:text-[#1A1A1A]"><X size={22} /></button>
            </div>

            <div className="p-5 space-y-5">
              {/* Mood — labelled 'How you feel?' only in the customer app */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#5C5A55] mb-2">
                  How you feel?
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {MOODS.map(m => (
                    <button key={m.key}
                            data-testid={`gm-mood-${m.key}`}
                            onClick={() => setMood(m.key)}
                            className={`p-2.5 rounded-md border text-center transition-colors ${mood === m.key ? "bg-[#DC2626] text-white border-[#DC2626]" : "border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                      <div className="text-2xl leading-none">{m.emoji}</div>
                      <div className="text-[10px] mt-1">{m.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#5C5A55] mb-2">
                  What&apos;s the issue about? (tap any that apply)
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="gm-tags">
                  {ISSUE_TAGS_LOCAL.map(t => (
                    <button key={t} onClick={() => toggleTag(t)}
                            data-testid={`gm-tag-${t.replace(/\s+/g,"-").toLowerCase()}`}
                            className={`text-xs px-2.5 h-8 rounded-full border transition-colors ${tags.includes(t) ? "bg-[#3B2C7F] text-white border-[#3B2C7F]" : "bg-white border-[#E5E2D9] text-[#5C5A55] hover:bg-[#F7F6F2]"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-[#5C5A55] block mb-1.5">
                  Describe the issue *
                </label>
                <textarea data-testid="gm-issue"
                          value={issue} onChange={e => setIssue(e.target.value)}
                          rows={5} maxLength={2000}
                          placeholder="Please share what happened — date, location, what you'd like resolved…"
                          className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md bg-white text-sm" />
                <div className="text-[10px] text-[#8A8780] mt-1 text-right">{issue.length}/2000</div>
              </div>

              {/* Iter 115 — Voice grievance */}
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-[#5C5A55] block mb-1.5">
                  Add a voice note <span className="text-[9px] normal-case text-[#8A8780]">optional · auto-deletes in 7 days</span>
                </label>
                <VoiceRecorder
                  blob={voiceBlob}
                  onCapture={setVoiceBlob}
                  onClear={() => { setVoiceBlob(null); setVoiceId(null); }}
                  uploading={voiceUploading}
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t border-[#E5E2D9] flex gap-2 justify-end">
              <button onClick={() => setOpen(false)}
                      className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
                Cancel
              </button>
              <button data-testid="gm-submit" disabled={busy} onClick={submit}
                      className="h-10 px-5 bg-[#C25D38] hover:bg-[#A54C2C] text-white rounded-md text-sm font-medium disabled:opacity-50">
                {busy ? "Submitting…" : "Submit grievance"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



// -------- Voice Recorder (Iter 115) --------
// Wraps browser MediaRecorder API. Enforces a 90-second cap so customers
// don't accidentally record a saga; the timer flashes when close to
// the ceiling. Emits a captured Blob upward for the parent to upload.
const MAX_VOICE_SECS = 90;
function VoiceRecorder({ blob, onCapture, onClear, uploading }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => () => {
    // Cleanup on unmount — close the mic stream if the user closed the
    // drawer mid-recording.
    if (recRef.current && recRef.current.state !== "inactive") {
      try { recRef.current.stop(); } catch { /* noop */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const start = async () => {
    setError(null);
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setError("Voice recording is not supported on this browser."); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime })
                        : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        onCapture(b);
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds(s => {
          const next = s + 1;
          if (next >= MAX_VOICE_SECS) stop();
          return next;
        });
      }, 1000);
    } catch (e) {
      setError("Microphone permission was denied. Enable it in your browser settings.");
    }
  };

  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") {
      try { recRef.current.stop(); } catch { /* noop */ }
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const clearRec = () => { onClear(); setSeconds(0); setError(null); };

  const blobUrl = blob && !recording ? URL.createObjectURL(blob) : null;
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  return (
    <div className="border border-[#E5E2D9] rounded-lg p-3 bg-[#FAFAF7]"
         data-testid="gm-voice-recorder">
      {!blob && !recording && (
        <button type="button" onClick={start}
                data-testid="gm-voice-start"
                className="w-full h-11 rounded-md bg-white border-2 border-dashed border-[#C25D38]/50 text-[#C25D38] hover:bg-[#FFF6F2] inline-flex items-center justify-center gap-2 text-sm font-medium">
          <Microphone size={16} weight="fill"/>Record voice note
        </button>
      )}
      {recording && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-[#C25D38]">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C25D38] opacity-75"/>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#C25D38]"/>
            </span>
            <span className="text-sm font-mono">
              {String(Math.floor(seconds/60)).padStart(2,"0")}:{String(seconds%60).padStart(2,"0")}
              <span className="text-[10px] text-[#8A8780] ml-1">/ {String(Math.floor(MAX_VOICE_SECS/60))}:{String(MAX_VOICE_SECS%60).padStart(2,"0")}</span>
            </span>
          </div>
          <button type="button" onClick={stop}
                  data-testid="gm-voice-stop"
                  className="h-9 px-3 rounded-md bg-[#C25D38] hover:bg-[#A54C2C] text-white text-sm font-medium inline-flex items-center gap-1.5">
            <Stop size={13} weight="fill"/>Stop
          </button>
        </div>
      )}
      {blob && !recording && (
        <div className="flex items-center gap-2">
          <audio controls src={blobUrl} data-testid="gm-voice-playback"
                  className="flex-1 min-w-0 h-10"/>
          <button type="button" onClick={clearRec} disabled={uploading}
                  data-testid="gm-voice-clear"
                  title="Discard and re-record"
                  className="h-9 w-9 rounded-md border border-[#E5E2D9] hover:bg-[#F7F6F2] text-[#7A2E18] flex items-center justify-center disabled:opacity-50">
            <Trash size={14} weight="bold"/>
          </button>
        </div>
      )}
      {error && (
        <div className="mt-2 text-[11px] text-[#7A2E18]" data-testid="gm-voice-error">
          {error}
        </div>
      )}
      {uploading && (
        <div className="mt-2 text-[11px] text-[#5C5A55]">Uploading voice note…</div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Iter 155 Phase 1 — Health-first daily strip
// Shows today's meds count, next vaccine, one-touch SOS
// ══════════════════════════════════════════════════════════════════
function TodaysCareStrip() {
  const [d, setD] = useState(null);
  const load = () => custApi.get("/me/home-dashboard").then(r => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;

  const pills = d.active_pills || [];
  const bookings = d.active_bookings || [];
  if (pills.length === 0 && bookings.length === 0) return null;

  const typeMeta = {
    doctor:    { Icon: Stethoscope,  color: "text-[#1D4ED8]", bg: "bg-[#DBEAFE]", label: "Doctor" },
    lab:       { Icon: TestTube,     color: "text-[#7C3AED]", bg: "bg-[#EDE9FE]", label: "Lab test" },
    ambulance: { Icon: Ambulance,    color: "text-[#DC2626]", bg: "bg-[#FEE2E2]", label: "Ambulance" },
  };

  const fmtTime = (iso) => {
    if (!iso) return "Any time";
    try {
      const dt = new Date(iso);
      return dt.toLocaleString("en-IN", {
        weekday: "short", day: "numeric", month: "short",
        hour: "numeric", minute: "2-digit",
      });
    } catch { return iso; }
  };

  const toggleDose = async (p) => {
    try {
      if (p.taken) {
        await custApi.delete(`/me/pill-reminders/${p.reminder_id}/log`,
                              { params: { time_slot: p.time } });
        toast.message(`Un-marked ${p.medicine_name} · ${p.time}`);
      } else {
        await custApi.post(`/me/pill-reminders/${p.reminder_id}/log`,
                            { time_slot: p.time });
        toast.success(`${p.medicine_name} · ${p.time} logged`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const activeAmbulance = bookings.filter(b => b.type === "ambulance"
    && ["confirmed", "dispatched", "arriving", "in_progress", "pending"].includes(b.status));

  return (
    <section className="px-5 mt-4 space-y-3" data-testid="me-today-care">
      {pills.length > 0 && (
        <div className="rounded-2xl border border-[#FCA5A5]/60 bg-gradient-to-br from-[#FEF2F2] to-white shadow-[0_8px_20px_rgba(220,38,38,0.06)] overflow-hidden">
          <Link to="/me/health/pills"
                data-testid="today-pills-header"
                className="flex items-center gap-2 px-4 py-2.5 border-b border-red-100/70">
            <div className="w-8 h-8 rounded-lg bg-[#DC2626] text-white flex items-center justify-center shrink-0">
              <Pill size={16} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#7F1D1D] font-bold">
                Today · Medications
              </div>
              <div className="text-[12px] font-semibold text-[#111827]">
                {pills.filter(p => p.taken).length}/{pills.length} doses taken
              </div>
            </div>
            <ArrowRight size={14} className="text-[#DC2626]"/>
          </Link>
          <ul className="divide-y divide-red-50">
            {pills.slice(0, 4).map(p => (
              <li key={p.id}
                  data-testid={`today-pill-${p.reminder_id}-${p.time}`}
                  className={`flex items-center gap-3 px-4 py-2.5 ${p.taken ? "bg-emerald-50/60" : ""}`}>
                <div className="w-[54px] shrink-0 text-center">
                  <div className="text-[9px] uppercase tracking-widest text-[#8A8780] font-bold">Dose</div>
                  <div className={`font-mono text-[13px] font-bold leading-tight ${p.taken ? "text-emerald-700 line-through" : "text-[#DC2626]"}`}
                        data-testid={`today-pill-time-${p.reminder_id}-${p.time}`}>
                    {p.time || "—"}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold truncate ${p.taken ? "text-[#374151]" : "text-[#111827]"}`}>
                    {p.medicine_name}
                  </div>
                  <div className="text-[11px] text-[#5C5A55] truncate">
                    {[p.dosage, p.loved_one_name].filter(Boolean).join(" · ") || "You"}
                    {p.taken && p.taken_at && (
                      <span className="ml-2 text-emerald-700 font-semibold">
                        · Taken at {new Date(p.taken_at).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleDose(p)}
                  data-testid={`today-pill-check-${p.reminder_id}-${p.time}`}
                  aria-label={p.taken ? "Un-mark dose" : "Mark dose as taken"}
                  className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition ${
                    p.taken
                      ? "bg-emerald-600 border-emerald-600 text-white shadow-sm"
                      : "border-[#DC2626] text-[#DC2626] hover:bg-[#FEE2E2]"
                  }`}>
                  {p.taken ? <CheckCircle size={18} weight="fill"/> : <Clock size={16} weight="bold"/>}
                </button>
              </li>
            ))}
          </ul>
          {pills.length > 4 && (
            <Link to="/me/health/pills"
                  className="block text-center text-[11px] font-bold text-[#DC2626] py-2 border-t border-red-50 hover:bg-[#FEF2F2]">
              +{pills.length - 4} more doses today →
            </Link>
          )}
        </div>
      )}

      {bookings.length > 0 && (
        <div className="rounded-2xl border border-[#DBEAFE] bg-gradient-to-br from-[#EFF6FF] to-white shadow-[0_8px_20px_rgba(29,78,216,0.06)] overflow-hidden">
          <Link to="/me/health/bookings"
                data-testid="today-bookings-header"
                className="flex items-center gap-2 px-4 py-2.5 border-b border-blue-100/70">
            <div className="w-8 h-8 rounded-lg bg-[#1D4ED8] text-white flex items-center justify-center shrink-0">
              <Calendar size={16} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E3A8A] font-bold">
                Active health service requests
              </div>
              <div className="text-[12px] font-semibold text-[#111827]">
                {bookings.length} appointment{bookings.length !== 1 ? "s" : ""} in progress
              </div>
            </div>
            <ArrowRight size={14} className="text-[#1D4ED8]"/>
          </Link>
          <ul className="divide-y divide-blue-50">
            {bookings.slice(0, 3).map(b => {
              const meta = typeMeta[b.type] || typeMeta.doctor;
              const Icon = meta.Icon;
              return (
                <Link
                  key={b.id}
                  to={`/me/health/bookings/${b.id}`}
                  data-testid={`today-booking-${b.id}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#EFF6FF]"
                >
                  <div className={`w-10 h-10 rounded-xl ${meta.bg} ${meta.color} flex items-center justify-center shrink-0`}>
                    <Icon size={16} weight="duotone"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#111827] truncate">
                      {b.provider_name || meta.label}
                    </div>
                    <div className="text-[11px] text-[#5C5A55] truncate">
                      {meta.label} · {b.no}
                    </div>
                    <div className="text-[10px] text-[#1D4ED8] font-mono mt-0.5">
                      {fmtTime(b.scheduled_at)}
                    </div>
                  </div>
                  <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-1 rounded ${
                    b.status === "in_progress" || b.status === "arriving" ? "bg-emerald-100 text-emerald-700"
                    : b.status === "dispatched" ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-600"
                  }`}>{(b.status || "").replace("_", " ")}</span>
                </Link>
              );
            })}
          </ul>

          {/* Live ambulance tracker(s) — only for in-flight ambulances */}
          {activeAmbulance.slice(0, 2).map(b => (
            <AmbulanceMiniTracker key={`trk-${b.id}`} bookingId={b.id}/>
          ))}
        </div>
      )}
    </section>
  );
}


function HealthDailyStrip() {
  const [d, setD] = useState(null);
  useEffect(() => {
    custApi.get("/me/home-dashboard").then(r => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return null;

  const familyCount = (d.family || []).length;
  const medsCount = d.active_pill_count || 0;
  const nextVac = (d.upcoming_vaccines || [])[0];
  const primaryEmg = (d.emergency_contacts || []).find(c => c.is_primary) || (d.emergency_contacts || [])[0];

  return (
    <section className="px-5 mt-4" data-testid="me-health-strip">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold">
          Today · Care
        </div>
        <Link to="/me/health" className="text-[11px] font-bold text-[#DC2626]">
          Open Health →
        </Link>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {/* SOS — one-touch ambulance */}
        <a href={`tel:${primaryEmg?.phone || "108"}`}
           data-testid="hs-sos"
           className="rounded-2xl p-3 bg-gradient-to-br from-[#DC2626] to-[#7F1D1D] text-white shadow-[0_10px_25px_rgba(220,38,38,0.30)] active:scale-[0.97] transition-transform">
          <FirstAidKit size={18} weight="fill" className="mb-1"/>
          <div className="text-[11px] font-bold leading-tight">SOS</div>
          <div className="text-[9px] uppercase tracking-widest opacity-90 mt-0.5">{primaryEmg?.phone || "108"}</div>
        </a>
        {/* Family count */}
        <Link to="/me/profile#health-profile" data-testid="hs-family"
              className="rounded-2xl p-3 bg-[#EDE9FE] border border-[#DDD6FE] active:scale-[0.97] transition-transform">
          <UsersThree size={18} weight="duotone" className="text-[#7C3AED] mb-1"/>
          <div className="font-display font-black text-lg text-[#7C3AED] leading-none">{familyCount}</div>
          <div className="text-[9px] uppercase tracking-widest font-bold text-[#7C3AED] mt-0.5">Family</div>
        </Link>
        {/* Meds today */}
        <Link to="/me/health/pills" data-testid="hs-meds"
              className="rounded-2xl p-3 bg-[#FEE2E2] border border-red-100 active:scale-[0.97] transition-transform">
          <Pill size={18} weight="duotone" className="text-[#DC2626] mb-1"/>
          <div className="font-display font-black text-lg text-[#DC2626] leading-none">{medsCount}</div>
          <div className="text-[9px] uppercase tracking-widest font-bold text-[#DC2626] mt-0.5">Meds on</div>
        </Link>
        {/* Next vaccine */}
        <Link to="/me/health/vaccines" data-testid="hs-vac"
              className="rounded-2xl p-3 bg-[#DBEAFE] border border-blue-100 active:scale-[0.97] transition-transform">
          <Certificate size={18} weight="duotone" className="text-[#1D4ED8] mb-1"/>
          <div className="font-display font-black text-lg text-[#1D4ED8] leading-none">
            {nextVac ? "1+" : "0"}
          </div>
          <div className="text-[9px] uppercase tracking-widest font-bold text-[#1D4ED8] mt-0.5">Vaccines</div>
        </Link>
      </div>
      {nextVac && (
        <Link to="/me/health/vaccines" data-testid="hs-next-vac-banner"
              className="mt-2 block rounded-xl p-3 bg-white border border-blue-100 hover:border-[#1D4ED8]">
          <div className="flex items-center gap-2">
            <Certificate size={14} className="text-[#1D4ED8]" weight="fill"/>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-[#111827] truncate">
                Next vaccine: <span className="text-[#1D4ED8]">{nextVac.vaccine_name}</span>
              </div>
              <div className="text-[10px] text-gray-500">Due {nextVac.due_date}</div>
            </div>
            <ArrowRight size={12} className="text-gray-400"/>
          </div>
        </Link>
      )}
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
// Iter 155 Phase 1 — Family dashboard strip
// Per-loved-one card with next action, vitals summary, quick log
// ══════════════════════════════════════════════════════════════════
function FamilyDashboardStrip() {
  const [d, setD] = useState(null);
  useEffect(() => {
    custApi.get("/me/home-dashboard").then(r => setD(r.data)).catch(() => {});
  }, []);
  if (!d || !d.family || d.family.length === 0) {
    return (
      <section className="px-5 mt-4" data-testid="me-family-empty">
        <Link to="/me/profile#health-profile"
              className="block rounded-2xl p-4 bg-white border-2 border-dashed border-[#DC2626] text-center">
          <UsersThree size={22} className="mx-auto text-[#DC2626] mb-1" weight="duotone"/>
          <div className="text-sm font-semibold text-[#111827]">Add your family</div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            Track health for parents, kids &amp; pets — all in one place
          </div>
        </Link>
      </section>
    );
  }

  const KIND_META = {
    baby:     { color: "#2563EB", label: "Baby",     go: "/me/health/growth",     action: "Growth" },
    elder:    { color: "#DC2626", label: "Elder",    go: "/me/health/elder",      action: "Care hub" },
    kid:      { color: "#7C3AED", label: "Kid",      go: "/me/health/kids",       action: "Habits" },
    woman:    { color: "#EC4899", label: "Woman",    go: "/me/health/cycle",      action: "Log cycle" },
    pregnant: { color: "#DB2777", label: "Pregnant", go: "/me/health/pregnancy",  action: "Kick log" },
    pet:      { color: "#0F766E", label: "Pet",      go: "/me/health/pet",        action: "Care" },
    self:     { color: "#7C3AED", label: "Self",     go: "/me/health/vitals",  action: "Log vitals" },
  };

  return (
    <section className="px-5 mt-4" data-testid="me-family-strip">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold">
          My Family · {d.family.length}
        </div>
        <Link to="/me/profile#health-profile" className="text-[11px] font-bold text-[#DC2626]">
          Manage →
        </Link>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-5 px-5" data-testid="me-family-scroll">
        {d.family.map(lo => {
          const meta = KIND_META[lo.kind || "self"] || KIND_META.self;
          const bp = lo.vitals_latest?.bp;
          const babyToday = lo.baby_today;
          return (
            <Link key={lo.id} to={meta.go}
                  data-testid={`fam-card-${lo.id}`}
                  className="shrink-0 w-[160px] bg-white rounded-2xl border border-gray-100 p-3 shadow-[0_4px_12px_rgba(0,0,0,0.04)] active:scale-[0.97] transition-transform">
              <div className="w-10 h-10 rounded-full text-white font-bold text-sm inline-flex items-center justify-center"
                   style={{ backgroundColor: meta.color }}>
                {lo.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="mt-2 font-semibold text-[13px] text-[#111827] truncate">{lo.name}</div>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: meta.color }}>
                {meta.label}{lo.age != null ? ` · ${lo.age}y` : ""}
              </div>
              {/* Kind-specific stat */}
              {lo.kind === "elder" && bp && (
                <div className="mt-1.5 text-[11px] text-gray-700">
                  BP <span className="font-mono font-bold">{bp.value_systolic}/{bp.value_diastolic}</span>
                </div>
              )}
              {lo.kind === "baby" && babyToday && (
                <div className="mt-1.5 text-[11px] text-gray-700">
                  {babyToday.feed || 0} feeds · {babyToday.diaper || 0} diapers
                </div>
              )}
              {lo.active_pill_count > 0 && (
                <div className="mt-1 text-[10px] text-[#DC2626] font-bold">
                  {lo.active_pill_count} med{lo.active_pill_count > 1 ? "s" : ""} active
                </div>
              )}
              {lo.next_vaccine && (
                <div className="mt-1 text-[10px] text-[#1D4ED8] truncate">
                  💉 {lo.next_vaccine.vaccine_name}
                </div>
              )}
              <div className="mt-2 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1" style={{ color: meta.color }}>
                {meta.action} <ArrowRight size={10} weight="bold"/>
              </div>
            </Link>
          );
        })}
        {/* Add card */}
        <Link to="/me/profile#health-profile"
              data-testid="fam-add"
              className="shrink-0 w-[120px] bg-white rounded-2xl border-2 border-dashed border-[#DC2626] p-3 flex flex-col items-center justify-center text-center">
          <Plus size={20} weight="bold" className="text-[#DC2626]"/>
          <div className="mt-1 text-[11px] font-semibold text-[#DC2626]">Add family</div>
        </Link>
      </div>
    </section>
  );
}
